import { useEffect, useRef, useState } from "react";
import { get } from "lodash";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money } from "@/lib/format";
import { TIPS } from "@/lib/tips";
import { Tip } from "@/components/Explain";

const toNum = (s) => (s === "" || s === "-" || s === "." ? "" : Number(s));
const group = (digits) => (digits ? Number(digits).toLocaleString("en-US") : "");

// "1234567.5" -> "1,234,567.5" while typing
const fmtTyped = (clean) => {
  const neg = clean.startsWith("-") ? "-" : "";
  const [i, d] = clean.replace("-", "").split(".");
  return `${neg}${group(i) || (d !== undefined ? "0" : "")}${d !== undefined ? `.${d}` : ""}`;
};

const fmtValue = (v, decimals) => {
  if (v === "" || v === null || v === undefined || Number.isNaN(Number(v))) return "";
  return fmtTyped(String(decimals ? Number(v) : Math.round(Number(v))));
};

export const NumInput = ({ id, value, onChange, placeholder, className = "", decimals = false }) => {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(fmtValue(value, decimals));
  useEffect(() => { if (!focused) setText(fmtValue(value, decimals)); }, [value, focused, decimals]);

  const handle = (e) => {
    const raw = e.target.value;
    const clean = raw.replace(/,/g, "");
    if (!(decimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/).test(clean)) return;
    const next = fmtTyped(clean);
    const keep = raw.slice(0, e.target.selectionStart ?? raw.length).replace(/,/g, "").length;
    setText(next);
    onChange(toNum(clean));
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el || document.activeElement !== el) return;
      let pos = 0;
      for (let n = 0; pos < next.length && n < keep; pos += 1) if (next[pos] !== ",") n += 1;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <Input ref={ref} id={id} data-testid={id} type="text" inputMode={decimals ? "decimal" : "numeric"} value={text} placeholder={placeholder}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} onChange={handle} className={`bg-white font-mono h-9 ${className}`} />
  );
};

export const YNToggle = ({ id, value, onChange, options = ["Y", "N"] }) => (
  <button type="button" data-testid={id} onClick={() => onChange(options[(options.indexOf(value) + 1) % options.length])}
    className={`yn-toggle ${value === "Y" ? "is-yes" : ""} ${value === "None" ? "is-gap" : ""}`}>{value || "-"}</button>
);

export const SelectInput = ({ id, value, onChange, options, className = "" }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger id={id} data-testid={id} className={`bg-white h-9 ${className}`}><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

export const Cell = ({ l, children, className = "" }) => <div className={`m-cell ${className}`} data-label={l}>{children}</div>;

export const ReqStar = ({ show }) => (show ? <span className="req-star" title="Required for calculations">*</span> : null);

export const FieldInput = ({ id, label, type = "text", value, onChange, options, wide, placeholder, hint, required, missing, tip }) => (
  <div className={`${wide ? "col-span-full " : ""}space-y-1 ${missing ? "field-missing" : ""}`} data-required={required ? "true" : undefined} data-missing={missing ? "true" : undefined}>
    <div className="flex items-center gap-1">
      <Label htmlFor={id} className="field-label">{label}<ReqStar show={required} />{hint && <span className="field-hint">{hint}</span>}</Label>
      <Tip text={tip} testId={`tip-${id}`} />
    </div>
    {options ? <SelectInput id={id} value={value} onChange={onChange} options={options} />
      : type === "textarea" ? <Textarea id={id} data-testid={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-white" rows={3} />
        : type === "number" || type === "money" ? <NumInput id={id} value={value} onChange={onChange} placeholder={placeholder} decimals={type === "number"} />
          : <Input id={id} data-testid={id} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-white h-9" />}
  </div>
);

export const ReadValue = ({ id, label, value, hint, required, missing, tip }) => (
  <div className={`space-y-1 ${missing ? "field-missing" : ""}`}>
    <div className="flex items-center gap-1"><span className="field-label">{label}<ReqStar show={required} />{hint && <span className="field-hint">{hint}</span>}</span><Tip text={tip} testId={`tip-${id}`} /></div>
    <div className="read-value font-mono" data-testid={id}>{value}</div>
  </div>
);

export const makeField = (d, set, req, prefix = "") => (path, label, type = "text", extra = {}) => {
  const base = path.replace(/\./g, "-");
  return <FieldInput key={path} id={`${prefix}${base}`} label={label} type={type} value={get(d, path)} onChange={(v) => set(path, v)}
    required={req?.all.has(base)} missing={req?.missing.has(base)} tip={TIPS[base]} {...extra} />;
};

export const HeadTip = ({ label }) => <span className="inline-flex items-center gap-1">{label}<Tip text={TIPS[label]} testId={`tip-head-${label.replace(/[^a-z]/gi, "").toLowerCase()}`} /></span>;

export const Panel = ({ title, right, children, className = "", testId, required, missing }) => (
  <section className={`panel ${className} ${missing ? "panel-missing" : ""}`} data-testid={testId}>
    {(title || right) && <div className="panel-head"><h3>{title}<ReqStar show={required} />{missing && <span className="missing-note">enter at least one amount</span>}</h3>{right}</div>}
    {children}
  </section>
);

export const ResultLine = ({ label, value, testId, strong, cur, hint }) => (
  <div className={`result-line ${strong ? "is-strong" : ""}`}>
    <span>{label}{hint && <span className="field-hint">{hint}</span>}</span><span className="font-mono" data-testid={testId}>{money(value, cur)}</span>
  </div>
);
